"use client"

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../../../components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { RefreshCw, Plus, Edit, Trash } from "lucide-react"
import { useState, useMemo } from "react"
import { Toast } from "../../../components/Toast"
import { submitBioData, deleteBioData } from "../../../api/adminService"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"

const ITEMS_PER_PAGE = 5

export function BioDataFormSection({
  bioDataList = [],
  setBioDataList,
  handleAdminSync,
  statusMessage,
  adminIsSyncing,
}: any) {
  const [selectedItem, setSelectedItem] = useState<any | null>(null)
  const [formData, setFormData] = useState<any>({})
  const [newItemOpen, setNewItemOpen] = useState(false)
  const [newForm, setNewForm] = useState<any>({
    name: "",
    gender: "",
    phone: "",
    email: "",
    birthday: "",
    instagram: "",
    other_social: "",
    address: "",
    city: "",
    state: "",
    country: "",
  })

  // 🔍 Search and Filter states
  const [searchTerm, setSearchTerm] = useState("")
  const [filterGender, setFilterGender] = useState("all")
  const [filterCountry, setFilterCountry] = useState("all")
  const [filterState, setFilterState] = useState("all")

  const [currentPage, setCurrentPage] = useState(1)

  // ✅ Derived filters
  const filteredList = useMemo(() => {
    return bioDataList.filter((item: any) => {
      const matchesSearch =
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.phone?.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesGender =
        filterGender === "all" ||
        item.gender?.toLowerCase() === filterGender.toLowerCase()

      const matchesCountry =
        filterCountry === "all" ||
        item.country?.toLowerCase() === filterCountry.toLowerCase()

      const matchesState =
        filterState === "all" ||
        item.state?.toLowerCase() === filterState.toLowerCase()

      return matchesSearch && matchesGender && matchesCountry && matchesState
    })
  }, [bioDataList, searchTerm, filterGender, filterCountry, filterState])

  const totalPages = Math.ceil(filteredList.length / ITEMS_PER_PAGE)
  const paginatedItems = filteredList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const handleCreate = async () => {
    try {
      const result = await submitBioData(newForm)
      setBioDataList((prev: any) => [result, ...prev])
      setNewForm({
        name: "",
        gender: "",
        phone: "",
        email: "",
        birthday: "",
        instagram: "",
        other_social: "",
        address: "",
        city: "",
        state: "",
        country: "",
      })
      setNewItemOpen(false)
      Toast.success("Bio Data created successfully")
    } catch (err) {
      Toast.error("Create failed: " + err)
    }
  }

  const handleUpdate = async () => {
    try {
      const result = await submitBioData(formData)
      setBioDataList((prev: any) =>
        prev.map((i: any) => (i.id === result.id ? result : i))
      )
      setSelectedItem(null)
      Toast.success("Bio Data updated successfully")
    } catch (err) {
      Toast.error("Update failed: " + err)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteBioData(id)
      setBioDataList((prev: any) => prev.filter((i: any) => i.id !== id))
      Toast.success("Bio Data deleted")
    } catch (err) {
      Toast.error("Delete failed: " + err)
    }
  }

  const handleDownloadPDF = () => {
    if (bioDataList.length === 0) {
      Toast.error("No data to export")
      return
    }

    const doc = new jsPDF()
    doc.setFontSize(16)
    doc.text("Bio Data Records", 14, 15)
    doc.setFontSize(10)
    doc.text(`Total Records: ${bioDataList.length}`, 14, 22)

    const tableData = bioDataList.map((item: any, index: number) => [
      index + 1,
      item.name || "—",
      item.gender || "—",
      item.phone || "—",
      item.email || "—",
      item.birthday || "—",
      item.city || "—",
      item.state || "—",
      item.country || "—",
    ])

    autoTable(doc, {
      startY: 28,
      head: [["#", "Name", "Gender", "Phone", "Email", "Birthday", "City", "State", "Country"]],
      body: tableData,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [33, 150, 243] },
    })

    doc.save("bio-data-list.pdf")
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                🧾 Bio Data Forms
              </CardTitle>
              <CardDescription>
                Manage members’ personal information and profiles
              </CardDescription>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPDF}
              >
                📄 Download PDF
              </Button>
              {/* <Button
                variant="outline"
                size="sm"
                onClick={handleAdminSync}
                disabled={adminIsSyncing}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-1 ${adminIsSyncing ? "animate-spin" : ""}`}
                />
                {adminIsSyncing ? "Syncing..." : "Refresh"}
              </Button> */}

              <Dialog open={newItemOpen} onOpenChange={setNewItemOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-1" /> New Bio Data
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create Bio Data</DialogTitle>
                    <DialogDescription>
                      Fill in the member’s information
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-3 py-2">
                    {Object.keys(newForm).map((key) => (
                      <div key={key} className="col-span-2 sm:col-span-1">
                        <label className="text-sm font-medium capitalize">
                          {key.replace("_", " ")}
                        </label>
                        <Input
                          value={newForm[key]}
                          onChange={(e) =>
                            setNewForm({ ...newForm, [key]: e.target.value })
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setNewItemOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleCreate}>Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* 🔍 Search + Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pb-2">
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-1/3"
            />
            <select
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="all">All Genders</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            <select
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="all">All Countries</option>
              {[...new Set(bioDataList.map((i: any) => i.country).filter(Boolean))].map(
                (country: any) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                )
              )}
            </select>
            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="all">All States</option>
              {[...new Set(bioDataList.map((i: any) => i.state).filter(Boolean))].map(
                (state: any) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                )
              )}
            </select>
          </div>

          {/* 🧾 List of BDFs */}
          <div className="space-y-4">
            {paginatedItems.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">
                No records found
              </p>
            ) : (
              paginatedItems.map((item: any) => (
                <div
                  key={item.id}
                  className="border rounded p-4 bg-muted/10 space-y-2"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold text-base">
                        {item.name} ({item.gender})
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {item.email} | {item.phone}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Dialog
                        open={!!selectedItem}
                        onOpenChange={(isOpen) =>
                          !isOpen && setSelectedItem(null)
                        }
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedItem(item)
                              setFormData({ ...item })
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                          <DialogHeader>
                            <DialogTitle>Edit Bio Data</DialogTitle>
                          </DialogHeader>
                          {selectedItem && (
                            <div className="grid grid-cols-2 gap-3 py-2">
                              {Object.keys(formData).map((key) =>
                                key === "id" || key === "date_created"
                                  ? null
                                  : (
                                    <div
                                      key={key}
                                      className="col-span-2 sm:col-span-1"
                                    >
                                      <label className="text-sm font-medium capitalize">
                                        {key.replace("_", " ")}
                                      </label>
                                      <Input
                                        value={formData[key] || ""}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            [key]: e.target.value,
                                          })
                                        }
                                      />
                                    </div>
                                  )
                              )}
                            </div>
                          )}
                          <div className="flex justify-end gap-2 pt-4">
                            <Button
                              variant="outline"
                              onClick={() => setSelectedItem(null)}
                            >
                              Cancel
                            </Button>
                            <Button onClick={handleUpdate}>Update</Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="text-sm text-muted-foreground grid grid-cols-2 gap-1">
                    <p><strong>Birthday:</strong> {item.birthday || "—"}</p>
                    <p><strong>Instagram:</strong> {item.instagram || "—"}</p>
                    <p><strong>City:</strong> {item.city || "—"}</p>
                    <p><strong>State:</strong> {item.state || "—"}</p>
                    <p><strong>Country:</strong> {item.country || "—"}</p>
                    <p className="col-span-2"><strong>Address:</strong> {item.address || "—"}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* 📄 Pagination */}
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
              {Math.min(currentPage * ITEMS_PER_PAGE, filteredList.length)} of{" "}
              {filteredList.length}
            </p>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                First
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                Last
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}